import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';

const ITEMS_PER_PAGE = 8;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(categoryArticles.length / ITEMS_PER_PAGE);
  const currentArticles = categoryArticles.slice(
    page * ITEMS_PER_PAGE,
    (page + 1) * ITEMS_PER_PAGE,
  );

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您访问的分类不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main className="pt-20">
        {/* Hero Banner */}
        <section className="relative py-12 md:py-16 px-4 md:px-6 bg-background-100 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-primary-500 blur-3xl"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer border-b border-transparent hover:border-primary-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-900 font-medium">{category.name}</span>
            </nav>

            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500">
                <i className={`${category.icon} text-background-50 text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.desc}</p>
              </div>
            </div>

            <p className="mt-4 text-xs text-foreground-400">
              共 <strong className="text-foreground-700">{categoryArticles.length}</strong> 篇文章
            </p>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {currentArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-10">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="上一页"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                          i === page
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page === totalPages - 1}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="下一页"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Other Categories */}
            <div className="mt-14 pt-10 border-t border-background-200">
              <h3 className="text-lg font-heading text-foreground-900 mb-5">探索更多分类</h3>
              <div className="flex flex-wrap gap-3">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-100 hover:text-primary-700 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.jinrichigua.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category.name,
                item: `https://www.jinrichigua.com/category/${category.slug}`,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 今日吃瓜`,
            url: `https://www.jinrichigua.com/category/${category.slug}`,
            description: `${category.name} - ${category.desc}，今日吃瓜分类页面，汇集最新${category.name}相关爆料与资讯。`,
            isPartOf: {
              '@type': 'WebSite',
              name: '今日吃瓜',
              url: 'https://www.jinrichigua.com/',
            },
          }),
        }}
      />
    </>
  );
}